import { Fragment, useContext } from 'react';
import { useSession } from 'contexts/SessionContext';
import { GearIcon } from '../../../../assets/icons';
import { useQuery } from '@apollo/client';
import Flyout, { useFlyout } from 'components/Flyout/Flyout';
import { GetInspectionFreeLimitDocument, GetInspectionsDocument, GetIntervalsForCraftDocument, IntervalSubtype, IntervalType, LogType } from 'graphql/generated';
import { EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/24/solid';
import New from './New';
import InspectionGroup from './Components/InspectionGroup';
import CraftIntervals from './Components/CraftsIntervals';
import Button from 'components/Button/Button';
import { DocumentCheckIcon } from '@heroicons/react/24/solid';
import { Menu, Transition } from '@headlessui/react';
import ADSFlyout from './ADS/ADSFlyout';
import { OrgSubtypes } from 'utils/orgTypes';
import isMobileApp from 'utils/isMobileApp';
import { Navigate, useNavigate } from 'react-router-dom';
import { AbilityContext } from 'contexts/AbilityContext';
import { permissionHandler } from 'utils/PermissionHandler';
import Intervals from '../Intervals';
import Header from 'components/Header/Header';
import CogMenu from 'components/CogMenu/CogMenu';
import ThumbButton from 'components/ThumbButton/ThumbButton';

const Status = function () {
  const { updateFlyout, flyoutProps, closeFlyout } = useFlyout();
  const { craftId, organizationId, organizationSubtype } = useSession();
  
  // check if craftId is undefined and navigate to crafts page
  const navigate = useNavigate();
  if(!craftId) navigate('/crafts');
  
  const { loading, error, data } = useQuery(GetInspectionsDocument, { variables: { craftId } });
  const { data : { craft } = {  } } = useQuery(GetIntervalsForCraftDocument, { variables: { craftId } });
  const { data: { organization : { inspectionFreeLimit } = {inspectionFreeLimit: undefined} } = { } } = useQuery( GetInspectionFreeLimitDocument, { variables: { id: organizationId } });
  const ability = useContext(AbilityContext);
  const numberOfInspections = data?.craft?.inspectionGroups.reduce((acc, group) => acc + group.inspections.length, 0);
  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error.toString()}</p>;
  
  const canManageInspections = permissionHandler('Manage Inspections', ability);
  const menuItems = [
    { 
      webAction: () => updateFlyout({ title: 'Import ADS', content: <ADSFlyout closeFlyout={closeFlyout} /> }),
      name: 'Import ADS from FAA',
      webOnly: false
    },
    { 
      mobileAction: async () => { await navigator.share({ title:"Export Inspections from Coflyt", url: process.env.REACT_APP_API_URL+"/inspections/"+craftId, text: "Export Inspections from Coflyt"}) },
      webAction: process.env.REACT_APP_API_URL+"/inspections/"+craftId,
      name: 'Export Inspections',
      webOnly: false
    },
  ]
  const groups = [...data?.craft?.inspectionGroups].sort((a, b) => a.position - b.position).map((group) => ({
    icon: GearIcon,
    color: 'blue',
    title: group.name,
    note: group.description,
    inspections: group.inspections,
    id: group.id,
    position: group.position,
  }));
  return (
    <>
      <Flyout {...flyoutProps} />
      <div className="flex flex-col w-full pb-0 md:pb-20">
        <Header title="Status" icon={DocumentCheckIcon}>
          <div className="flex items-stretch gap-2">
            {canManageInspections && 
              <>
                <Button className="hidden md:flex" text="New Group" color="navy" size="xs" icon={<PlusIcon className="h-4 w-4 mr-1" />} onClick={ () => updateFlyout({ title: 'Add Inspection Group', content: <New closeFlyout={closeFlyout} /> }) } /> 
                <ThumbButton size="primary" role="add" onClick={ () => updateFlyout({ title: 'Add Inspection Group', content: <New closeFlyout={closeFlyout} /> }) } />
              </>
            }
            <CogMenu items={menuItems} />
          </div>
        </Header>
        <CraftIntervals intervals={craft?.normalIntervals.filter((interval) => interval.intervalType === IntervalType.Normal).filter((interval) => {
            if( craft.loggingOption === LogType.Loghobbs && (interval.intervalSubType === IntervalSubtype.Tach || interval.intervalSubType === IntervalSubtype.Tachtwo  ) ) return false;
            if( craft.loggingOption === LogType.Logtach && interval.intervalSubType === IntervalSubtype.Hobbs ) return false;
            return true;
          }).sort((a, b) => a.name.localeCompare(b.name) ).map((interval) => ( {
            name: interval?.name,
            value: interval?.recentIntervalLog?.endValue ?? 0,
          } ))} />
        <div className="flex flex-col gap-2">
          {groups.map((group, index) => {
            return <InspectionGroup disableAdding={ organizationSubtype === OrgSubtypes.FREE && inspectionFreeLimit <= numberOfInspections } key={index} group={group} updateFlyout={updateFlyout} closeFlyout={closeFlyout} />;
          })}
        </div>
      </div>
    </>
  );
};

export default Status;
